import { createSimpleContext } from "@openanalyst-ai/ui/context"
import { base64Encode } from "@openanalyst-ai/core/util/encode"
import { useParams } from "@solidjs/router"
import { batch, createEffect, createMemo } from "solid-js"
import { createStore } from "solid-js/store"
import { useModels } from "@/context/models"
import { useProviders } from "@/hooks/use-providers"
import { Persist, persisted } from "@/utils/persist"
import { getOpenAnalystAccountModelFromToken } from "./auth/token"
import { parseConfiguredModel, resolveCurrentModel, type ModelKey } from "./local-model"
import {
  cycleModelVariant,
  getConfiguredAgentVariant,
  resolveModelVariant,
  selectedVariantWithPreference,
} from "./model-variant"
import { useSDK } from "./sdk"
import { useSync } from "./sync"

export type { ModelKey } from "./local-model"

type State = {
  agent?: string
  agentActive?: boolean
  model?: ModelKey
  variant?: string | null
}

type Saved = {
  session: Record<string, State | undefined>
  // The reasoning variant the user last picked, kept as a workspace preference
  // so it carries into new sessions instead of resetting to the model default.
  lastVariant?: string | null
}

const WORKSPACE_KEY = "__workspace__"
const handoff = new Map<string, State>()

const handoffKey = (dir: string, id: string) => `${dir}\n${id}`

export const DEFAULT_COMPOSER_AGENT = "build"

export function resolveComposerAgentName<T extends { name: string }>(items: readonly T[], current?: string) {
  if (items.some((item) => item.name === current)) return current
  return items.find((item) => item.name === DEFAULT_COMPOSER_AGENT)?.name ?? items[0]?.name
}

export function resolveActiveComposerAgentName<T extends { name: string }>(
  items: readonly T[],
  state: { agent?: string; agentActive?: boolean } | undefined,
) {
  if (!state?.agent) return undefined
  if (state.agentActive !== true) return undefined
  return items.some((item) => item.name === state.agent) ? state.agent : undefined
}

const migrate = (value: unknown) => {
  if (!value || typeof value !== "object") return { session: {} }

  const item = value as {
    session?: Record<string, State | undefined>
    pick?: Record<string, State | undefined>
    lastVariant?: string | null
  }
  const lastVariant = item.lastVariant

  if (item.session && typeof item.session === "object") return { session: item.session, lastVariant }
  if (!item.pick || typeof item.pick !== "object") return { session: {}, lastVariant }

  return {
    session: Object.fromEntries(Object.entries(item.pick).filter(([key]) => key !== WORKSPACE_KEY)),
    lastVariant,
  }
}

const clone = (value: State | undefined) => {
  if (!value) return undefined
  return {
    ...value,
    model: value.model ? { ...value.model } : undefined,
  } satisfies State
}

export const {
  use: useLocal,
  // The onboarding first-run dialog is mounted with the Layout owner, which sits
  // ABOVE LocalProvider (that lives under SDK/Sync, per directory), so it can only
  // use the non-throwing accessor.
  useOptional: useLocalOptional,
  provider: LocalProvider,
} = createSimpleContext({
  name: "Local",
  init: () => {
    const params = useParams()
    const sdk = useSDK()
    const sync = useSync()
    const providers = useProviders()
    const models = useModels()

    const id = createMemo(() => params.id || undefined)
    const list = createMemo(() => sync.data.agent.filter((item) => item.mode !== "subagent" && !item.hidden))
    const connected = createMemo(() => new Set(providers.connected().map((item) => item.id)))

    const [saved, setSaved] = persisted(
      {
        ...Persist.workspace(sdk.directory, "model-selection", ["model-selection.v1"]),
        migrate,
      },
      createStore<Saved>({
        session: {},
      }),
    )

    const [store, setStore] = createStore<{
      current?: string
      draft?: State
      last?: {
        type: "agent" | "model" | "variant"
        agent?: string
        model?: ModelKey | null
        variant?: string | null
      }
    }>({
      current: resolveComposerAgentName(list()),
      draft: undefined,
      last: undefined,
    })

    const validModel = (model: ModelKey) => {
      const provider = providers.all().find((item) => item.id === model.providerID)
      return !!provider?.models[model.modelID] && connected().has(model.providerID) && models.visible(model)
    }

    const pickAgent = (name: string | undefined) => {
      const items = list()
      if (items.length === 0) return undefined
      return items.find((item) => item.name === resolveComposerAgentName(items, name))
    }

    createEffect(() => {
      const items = list()
      if (items.length === 0) {
        if (store.current !== undefined) setStore("current", undefined)
        return
      }
      const next = resolveComposerAgentName(items, store.current)
      if (store.current === next) return
      setStore("current", next)
    })

    const scope = createMemo<State | undefined>(() => {
      const session = id()
      if (!session) return store.draft
      return saved.session[session] ?? handoff.get(handoffKey(sdk.directory, session))
    })

    const activeAgentName = () => resolveActiveComposerAgentName(list(), scope())

    createEffect(() => {
      const session = id()
      if (!session) return

      const key = handoffKey(sdk.directory, session)
      const next = handoff.get(key)
      if (!next) return
      if (saved.session[session] !== undefined) {
        handoff.delete(key)
        return
      }

      setSaved("session", session, clone(next))
      handoff.delete(key)
    })

    const configuredModel = () => {
      if (!sync.data.config.model) return
      const model = parseConfiguredModel(sync.data.config.model)
      if (!model) return
      if (validModel(model)) return model
    }

    const openanalystAccountModel = () => {
      const claimed = getOpenAnalystAccountModelFromToken()
      if (claimed && validModel(claimed)) return claimed

      const provider = providers.connected().find((item) => item.id === "openanalyst")
      if (!provider) return undefined

      const configured = providers.default().openanalyst
      if (configured) {
        const model = { providerID: provider.id, modelID: configured }
        if (validModel(model)) return model
      }
    }

    const agent = {
      list,
      current() {
        return pickAgent(scope()?.agent ?? store.current)
      },
      active() {
        const name = activeAgentName()
        if (!name) return undefined
        return list().find((item) => item.name === name)
      },
      set(name: string | undefined) {
        const item = pickAgent(name)
        if (!item) {
          setStore("current", undefined)
          return
        }

        batch(() => {
          setStore("current", item.name)
          setStore("last", {
            type: "agent",
            agent: item.name,
            model: item.model,
            variant: item.variant ?? null,
          })
          const prev = scope()
          const next = {
            agent: item.name,
            agentActive: true,
            model: item.model ?? prev?.model,
            variant: item.variant ?? prev?.variant,
          } satisfies State
          const session = id()
          if (session) {
            setSaved("session", session, next)
            return
          }
          setStore("draft", next)
        })
      },
      clear() {
        batch(() => {
          setStore("current", resolveComposerAgentName(list()))
          setStore("last", {
            type: "agent",
            agent: undefined,
            model: scope()?.model ?? null,
            variant: scope()?.variant ?? null,
          })
          const next = {
            ...(scope() ?? {}),
            agent: undefined,
            agentActive: false,
          } satisfies State
          const session = id()
          if (session) {
            setSaved("session", session, next)
            return
          }
          setStore("draft", next)
        })
      },
      move(direction: 1 | -1) {
        const items = list()
        if (items.length === 0) {
          setStore("current", undefined)
          return
        }

        let next = items.findIndex((item) => item.name === agent.current()?.name) + direction
        if (next < 0) next = items.length - 1
        if (next >= items.length) next = 0
        const item = items[next]
        if (!item) return
        agent.set(item.name)
      },
    }

    const fallback = createMemo<ModelKey | undefined>(() =>
      resolveCurrentModel({
        scopeModel: scope()?.model,
        agentModel: agent.current()?.model,
        configuredModel: configuredModel(),
        recentModels: models.recent.list(),
        openanalystModel: openanalystAccountModel(),
        connectedProviders: providers.connected().map((provider) => ({
          id: provider.id,
          models: Object.values(provider.models).map((model) => ({ id: model.id })),
        })),
        defaults: providers.default(),
        isModelValid: validModel,
      }),
    )

    const current = () => {
      const item = fallback()
      if (!item) return undefined
      return models.find(item)
    }

    const configured = () => {
      const item = agent.current()
      const model = current()
      if (!item || !model) return undefined
      return getConfiguredAgentVariant({
        agent: { model: item.model, variant: item.variant },
        model: { providerID: model.provider.id, modelID: model.id, variants: model.variants },
      })
    }

    const selected = () => scope()?.variant

    const snapshot = () => {
      const model = current()
      const active = activeAgentName()
      return {
        agent: active,
        agentActive: !!active,
        model: model ? { providerID: model.provider.id, modelID: model.id } : undefined,
        variant: selected(),
      } satisfies State
    }

    const write = (next: Partial<State>) => {
      const state = {
        ...(scope() ?? { agent: agent.current()?.name }),
        ...next,
      } satisfies State

      const session = id()
      if (session) {
        setSaved("session", session, state)
        return
      }
      setStore("draft", state)
    }

    const recent = createMemo(() => models.recent.list().map(models.find).filter(Boolean))

    const model = {
      ready: models.ready,
      current,
      recent,
      list: models.list,
      cycle(direction: 1 | -1) {
        const items = recent()
        const item = current()
        if (!item) return

        const index = items.findIndex((entry) => entry?.provider.id === item.provider.id && entry?.id === item.id)
        if (index === -1) return

        let next = index + direction
        if (next < 0) next = items.length - 1
        if (next >= items.length) next = 0

        const entry = items[next]
        if (!entry) return
        model.set({ providerID: entry.provider.id, modelID: entry.id })
      },
      set(item: ModelKey | undefined, options?: { recent?: boolean }) {
        batch(() => {
          setStore("last", {
            type: "model",
            agent: agent.current()?.name,
            model: item ?? null,
            variant: selected(),
          })
          write({ model: item })
          if (!item) return
          models.setVisibility(item, true)
          if (!options?.recent) return
          models.recent.push(item)
        })
      },
      visible(item: ModelKey) {
        return models.visible(item)
      },
      setVisibility(item: ModelKey, visible: boolean) {
        models.setVisibility(item, visible)
      },
      variant: {
        configured,
        selected,
        current() {
          return resolveModelVariant({
            variants: this.list(),
            selected: selectedVariantWithPreference(this.selected(), saved.lastVariant),
            configured: this.configured(),
          })
        },
        list() {
          const item = current()
          if (!item?.variants) return []
          return Object.keys(item.variants)
        },
        set(value: string | undefined) {
          batch(() => {
            const model = current()
            setStore("last", {
              type: "variant",
              agent: agent.current()?.name,
              model: model ? { providerID: model.provider.id, modelID: model.id } : null,
              variant: value ?? null,
            })
            write({ variant: value ?? null })
            // Remember this choice as the workspace-wide reasoning preference.
            setSaved("lastVariant", value ?? null)
          })
        },
        cycle() {
          const items = this.list()
          if (items.length === 0) return
          this.set(
            cycleModelVariant({
              variants: items,
              selected: this.selected(),
              configured: this.configured(),
            }),
          )
        },
      },
    }

    const result = {
      slug: createMemo(() => base64Encode(sdk.directory)),
      model,
      agent,
      session: {
        reset() {
          setStore("draft", undefined)
        },
        promote(dir: string, session: string) {
          const next = clone(snapshot())
          if (!next) return

          if (dir === sdk.directory) {
            setSaved("session", session, next)
            setStore("draft", undefined)
            return
          }

          handoff.set(handoffKey(dir, session), next)
          setStore("draft", undefined)
        },
        restore(msg: { sessionID: string; agent: string; model: ModelKey }) {
          const session = id()
          if (!session) return
          if (msg.sessionID !== session) return
          if (saved.session[session] !== undefined) return
          if (handoff.has(handoffKey(sdk.directory, session))) return

          setSaved("session", session, {
            agent: msg.agent === DEFAULT_COMPOSER_AGENT ? undefined : msg.agent,
            agentActive: msg.agent !== DEFAULT_COMPOSER_AGENT,
            model: msg.model,
            variant: msg.model?.variant ?? null,
          })
        },
      },
    }
    return result
  },
})
